import React, { useState, useEffect, useCallback } from "react";

interface NumberPadProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

const OPS = ["+", "-", "*", "/"];
const isOp = (c: string) => OPS.includes(c);

function evaluate(expr: string): string | null {
  try {
    const result = Function('"use strict"; return (' + expr + ")")();
    if (typeof result !== "number" || !isFinite(result)) return null;
    return parseFloat(result.toFixed(10)).toString();
  } catch {
    return null;
  }
}

function hasOperator(expr: string): boolean {
  for (let i = 1; i < expr.length; i++) {
    if (isOp(expr[i])) return true;
  }
  return false;
}

const NumberPad: React.FC<NumberPadProps> = ({ value, onChange, onClose }) => {
  const [expr, setExpr] = useState<string>(value || "");
  const [fresh, setFresh] = useState(true);

  useEffect(() => {
    setExpr(value || "");
    setFresh(true);
  }, [value]);

  const preview = hasOperator(expr) ? evaluate(expr) : null;

  const handlePress = useCallback(
    (key: string) => {
      if (key === "C") { setExpr(""); setFresh(true); return; }

      if (key === "back") { setExpr((p) => p.slice(0, -1)); setFresh(false); return; }

      if (key === "ok") {
        const result = hasOperator(expr) ? evaluate(expr) : expr;
        onChange(result ?? expr);
        onClose();
        return;
      }

      if (key === "%") {
        if (!expr || isOp(expr[expr.length - 1])) return;
        const result = evaluate(expr);
        if (result !== null) setExpr((parseFloat(result) / 100).toString());
        return;
      }

      if (isOp(key)) {
        setExpr((p) => {
          if (!p) return key === "-" ? "-" : p;
          if (isOp(p[p.length - 1])) return p.slice(0, -1) + key;
          return p + key;
        });
        setFresh(false);
        return;
      }

      if (key === ".") {
        const parts = expr.split(/[+\-*/]/);
        if (parts[parts.length - 1].includes(".")) return;
        setExpr((p) => {
          const prefix = !p || isOp(p[p.length - 1]) ? p + "0" : p;
          return prefix + ".";
        });
        setFresh(false);
        return;
      }

      if (fresh && !isOp(expr[expr.length - 1]) && expr !== "") {
        setExpr(key); setFresh(false);
      } else {
        setExpr((p) => p + key); setFresh(false);
      }
    },
    [expr, fresh, onChange, onClose]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") handlePress(e.key);
      else if (e.key === ".") handlePress(".");
      else if (["+", "-", "*", "/"].includes(e.key)) handlePress(e.key);
      else if (e.key === "%") handlePress("%");
      else if (e.key === "Backspace") handlePress("back");
      else if (e.key === "Delete") handlePress("C");
      else if (e.key === "Enter") handlePress("ok");
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handlePress, onClose]);

  // Base button: h-16 = 64px tall, text-xl
  const base = "flex items-center justify-center h-16 rounded-xl border border-gray-200 text-xl font-medium transition-all active:scale-95 cursor-pointer select-none";

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-slate-200 rounded-2xl p-6 w-80 shadow-inner">

        {/* Display */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-5 min-h-[84px] flex flex-col justify-end text-right">
          <div className="text-sm text-gray-400 font-mono min-h-[18px]">{expr}</div>
          <div className="text-3xl font-medium text-gray-900 font-mono leading-tight">
            {expr || "0"}
          </div>
          <div className="text-sm text-emerald-600 font-mono min-h-[20px]">
            {preview !== null ? `= ${preview}` : ""}
          </div>
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-4 gap-2.5">
          <button className={`${base} bg-red-50 border-red-200 text-red-700`} onClick={() => handlePress("C")}>C</button>
          <button className={`${base} bg-gray-100 text-gray-500`} onClick={() => handlePress("back")}>⌫</button>
          <button className={`${base} bg-gray-100 text-gray-600`} onClick={() => handlePress("%")}>%</button>
          <button className={`${base} bg-gray-100 text-gray-600`} onClick={() => handlePress("/")}>÷</button>

          {["7", "8", "9"].map(n => (
            <button key={n} className={`${base} bg-white`} onClick={() => handlePress(n)}>{n}</button>
          ))}
          <button className={`${base} bg-gray-100 text-gray-600`} onClick={() => handlePress("*")}>×</button>

          {["4", "5", "6"].map(n => (
            <button key={n} className={`${base} bg-white`} onClick={() => handlePress(n)}>{n}</button>
          ))}
          <button className={`${base} bg-gray-100 text-gray-600`} onClick={() => handlePress("-")}>−</button>

          {["1", "2", "3"].map(n => (
            <button key={n} className={`${base} bg-white`} onClick={() => handlePress(n)}>{n}</button>
          ))}
          <button className={`${base} bg-gray-100 text-gray-600`} onClick={() => handlePress("+")}>+</button>

          <button className={`${base} bg-white col-span-2`} onClick={() => handlePress("0")}>0</button>
          <button className={`${base} bg-white`} onClick={() => handlePress(".")}>.</button>
          <button
            className={`${base} bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700`}
            onClick={() => handlePress("ok")}
          >
            OK
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">keyboard · Esc to cancel</p>
      </div>
    </div>
  );
};

export default NumberPad;